import type {
  Round,
  TableAssignment,
  RoundBye,
  GeneratePairingsRequest,
} from '@swiss-magic/shared-types';
import { get, post, patch, del } from './core';

export function getRounds(tournamentId: string): Promise<Round[]> {
  return get(`/tournaments/${tournamentId}/rounds`);
}

// Story 105 — active players who have secured a TOP X place (KO qualifier
// mode). Empty for non-KO systems and when koFinishTop is 1.
export function getSecuredPlayerIds(
  tournamentId: string,
): Promise<{ securedPlayerIds: string[] }> {
  return get(`/tournaments/${tournamentId}/rounds/secured`);
}

export function generateAllRounds(tournamentId: string): Promise<Round[]> {
  return post(`/tournaments/${tournamentId}/rounds/generate-all`);
}

export function generatePairings(
  tournamentId: string,
  forceWithIncomplete = false,
  byes?: RoundBye[],
): Promise<Round> {
  const body: GeneratePairingsRequest = {};
  if (forceWithIncomplete) body.forceWithIncomplete = true;
  if (byes && byes.length > 0) body.byes = byes;
  return post(
    `/tournaments/${tournamentId}/rounds/generate`,
    Object.keys(body).length > 0 ? body : undefined,
  );
}

export function updateTableAssignments(
  tournamentId: string,
  roundId: string,
  assignments: TableAssignment[],
): Promise<Round> {
  return patch(
    `/tournaments/${tournamentId}/rounds/${roundId}/table-assignments`,
    { assignments },
  );
}

export function publishRound(tournamentId: string, roundId: string): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/publish`);
}

// Story 096 — set a round's date for the FIDE TRF report (YYYY-MM-DD or null).
export function setRoundDate(
  tournamentId: string,
  roundId: string,
  roundDate: string | null,
): Promise<Round> {
  return patch(`/tournaments/${tournamentId}/rounds/${roundId}/date`, {
    roundDate,
  });
}

export function setResult(
  tournamentId: string,
  roundId: string,
  pairingId: string,
  result: string,
): Promise<Round> {
  return patch(
    `/tournaments/${tournamentId}/rounds/${roundId}/pairings/${pairingId}/result`,
    { result },
  );
}

export function confirmResult(
  tournamentId: string,
  roundId: string,
  pairingId: string,
): Promise<Round> {
  return post(
    `/tournaments/${tournamentId}/rounds/${roundId}/pairings/${pairingId}/confirm`,
  );
}

export function completeRound(tournamentId: string, roundId: string): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/complete`);
}

export function voidResult(
  tournamentId: string,
  roundId: string,
  pairingId: string,
): Promise<Round> {
  return post(
    `/tournaments/${tournamentId}/rounds/${roundId}/pairings/${pairingId}/void`,
  );
}

// Story 081 — flip the optional In Progress flag on a pairing.
export function setPairingStarted(
  tournamentId: string,
  roundId: string,
  pairingId: string,
  started: boolean,
): Promise<Round> {
  return patch(
    `/tournaments/${tournamentId}/rounds/${roundId}/pairings/${pairingId}/started`,
    { started },
  );
}

export function unpublishRound(tournamentId: string, roundId: string): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/unpublish`);
}

export function swapPlayers(
  tournamentId: string,
  roundId: string,
  playerIdA: string,
  playerIdB: string,
): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/swap-players`, {
    playerIdA,
    playerIdB,
  });
}

export function createManualRound(
  tournamentId: string,
  forceWithIncomplete?: boolean,
): Promise<Round> {
  return post(
    `/tournaments/${tournamentId}/rounds/create-manual`,
    forceWithIncomplete ? { forceWithIncomplete } : undefined,
  );
}

export function uncompleteRound(tournamentId: string, roundId: string): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/uncomplete`);
}

export function deleteRound(tournamentId: string, roundId: string): Promise<void> {
  return del(`/tournaments/${tournamentId}/rounds/${roundId}`);
}

export function addManualPairing(
  tournamentId: string,
  roundId: string,
  whitePlayerId: string,
  blackPlayerId: string | null,
): Promise<Round> {
  return post(`/tournaments/${tournamentId}/rounds/${roundId}/pairings`, {
    whitePlayerId,
    blackPlayerId,
  });
}
